import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  Box,
  Typography,
  Button,
  Grid,
  Card,
  CardMedia,
  CardContent,
  CardActions,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  InputAdornment,
  Menu,
} from '@mui/material';
import {
  Add as AddIcon,
  Download as ExportIcon,
  Delete as DeleteIcon,
  Close as CloseIcon,
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  ContentCopy as DuplicateIcon,
} from '@mui/icons-material';
import {
  RiGridFill,
  RiBuildingLine,
  RiFolderOpenLine,
  RiArtboard2Line,
  RiStore2Fill,
} from '@remixicon/react';
import { fetchMenus, createMenu, removeMenu, exportAndUploadMenu } from '../redux/slices/menuSlice';
import { useNavigate } from 'react-router-dom';
import { colors, borderRadius, naturalScale, primaryScale } from '../theme/tokens';
import emptyFolderImage from '../assets/images/empty-folder.svg';
import { readCanvasConfig, updateCanvasConfig, deleteCanvasConfig } from '../services/s3Service';
import { loadProject } from '../canvas/redux/slices/canvasSlice';

const MenuBuilder = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { menus, templates, loading, error, exportStatus } = useSelector((state) => state.menu);
  const { takeawayList } = useSelector((state) => state.auth);
  const [activeTab, setActiveTab] = useState(0);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [selectedBranch, setSelectedBranch] = useState('');
  const [projectName, setProjectName] = useState('');
  const [canvases, setCanvases] = useState([]);
  const [loadingCanvases, setLoadingCanvases] = useState(false);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [selectedCanvas, setSelectedCanvas] = useState(null);
  const userId = 'demo-user'; // Replace with actual user ID

  useEffect(() => {
    dispatch(fetchMenus(userId));
  }, [dispatch, userId]);

  // Fetch canvases when "My Projects" tab is active
  useEffect(() => {
    if (activeTab === 0 && takeawayList && takeawayList.length > 0) {
      fetchAllCanvases();
    }
  }, [activeTab, takeawayList]);

  const fetchAllCanvases = async () => {
    setLoadingCanvases(true);
    try {
      const allCanvases = [];
      
      // Fetch canvases from all branches
      for (const branch of takeawayList) {
        const storeId = branch.store_id || branch.id;
        if (storeId) {
          try {
            const branchCanvases = await readCanvasConfig(storeId);
            // Add branch info to each canvas
            const canvasesWithBranch = branchCanvases.map(canvas => ({
              ...canvas,
              branchId: storeId,
              branchName: branch.name || branch.store_name || `Branch ${storeId}`,
            }));
            allCanvases.push(...canvasesWithBranch);
          } catch (error) {
            console.warn(`Failed to fetch canvases for branch ${storeId}:`, error);
          }
        }
      }
      
      // Sort by savedAt (most recent first)
      allCanvases.sort((a, b) => {
        const dateA = a.savedAt ? new Date(a.savedAt) : new Date(0);
        const dateB = b.savedAt ? new Date(b.savedAt) : new Date(0);
        return dateB - dateA;
      });
      
      setCanvases(allCanvases);
    } catch (error) {
      console.error('Error fetching canvases:', error);
    } finally {
      setLoadingCanvases(false);
    }
  };

  const handleCreateProject = () => {
    if (!projectName.trim()) return;

    // Close modal and navigate to canvas editor
    setCreateModalOpen(false);
    setProjectName('');
    setSelectedBranch('');
    navigate('/canvas-editor', {
      state: {
        projectName,
        branchId: selectedBranch,
      },
    });
  };

  const handleExportMenu = async (menuId) => {
    try {
      await dispatch(
        exportAndUploadMenu({
          userId,
          menuId,
          format: 'image',
        })
      ).unwrap();
      alert('Menu exported and uploaded to S3 successfully!');
    } catch (error) {
      console.error('Export failed:', error);
    }
  };

  const handleDeleteMenu = async (menuId) => {
    if (window.confirm('Are you sure you want to delete this menu?')) {
      try {
        await dispatch(removeMenu({ userId, menuId })).unwrap();
      } catch (error) {
        console.error('Delete failed:', error);
      }
    }
  };

  const handleMenuOpen = (event, canvas) => {
    setMenuAnchorEl(event.currentTarget);
    setSelectedCanvas(canvas);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setSelectedCanvas(null);
  };

  const handleEditCanvas = () => {
    if (!selectedCanvas) return;
    
    // Load canvas data into Redux
    dispatch(loadProject(selectedCanvas));
    
    // Navigate to canvas editor
    navigate('/canvas-editor', {
      state: {
        projectName: selectedCanvas.projectName || 'Untitled Menu',
        branchId: selectedCanvas.branchId,
        canvasId: selectedCanvas.id,
      },
    });
    
    handleMenuClose();
  };

  const handleDuplicateCanvas = async () => {
    if (!selectedCanvas) return;
    
    try {
      // Create a copy with new ID
      const newCanvas = {
        ...selectedCanvas,
        id: `canvas-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        projectName: `${selectedCanvas.projectName || 'Untitled Menu'} (Copy)`,
        savedAt: new Date().toISOString(),
      };
      
      // Remove canvasId from Redux state (will be set on save)
      delete newCanvas.canvasId;
      
      // Save the duplicate
      await updateCanvasConfig(selectedCanvas.branchId, newCanvas);
      
      // Refresh the list
      await fetchAllCanvases();
      
      alert('Canvas duplicated successfully!');
    } catch (error) {
      console.error('Error duplicating canvas:', error);
      alert(`Failed to duplicate canvas: ${error.message}`);
    }
    
    handleMenuClose();
  };

  const handleDeleteCanvas = async () => {
    if (!selectedCanvas) return;
    
    if (window.confirm('Are you sure you want to delete this canvas?')) {
      try {
        await deleteCanvasConfig(selectedCanvas.branchId, selectedCanvas.id);
        
        // Refresh the list
        await fetchAllCanvases();
        
        alert('Canvas deleted successfully!');
      } catch (error) {
        console.error('Error deleting canvas:', error);
        alert(`Failed to delete canvas: ${error.message}`);
      }
    }
    
    handleMenuClose();
  };

  // Canvas Preview Component
  const CanvasPreview = ({ canvas }) => {
    const bgColor = canvas.background?.type === 'color' 
      ? canvas.background.value 
      : canvas.background?.type === 'image' 
        ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
        : '#FFFFFF';
    
    const bgImage = canvas.background?.type === 'image' ? canvas.background.value : null;
    
    return (
      <Box
        sx={{
          width: '100%',
          height: 200,
          background: bgImage 
            ? `url(${bgImage}) center/cover` 
            : bgColor,
          borderRadius: borderRadius.xxxs,
          position: 'relative',
          overflow: 'hidden',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {/* Show first few elements as preview */}
        {canvas.elements && canvas.elements.length > 0 && (
          <Box
            sx={{
              position: 'absolute',
              top: 8,
              left: 8,
              right: 8,
              bottom: 8,
              pointerEvents: 'none',
            }}
          >
            {canvas.elements.slice(0, 3).map((element, idx) => (
              <Box
                key={idx}
                sx={{
                  position: 'absolute',
                  left: `${(element.position?.x || 0) / (canvas.dimensions?.[canvas.orientation]?.width || 1920) * 100}%`,
                  top: `${(element.position?.y || 0) / (canvas.dimensions?.[canvas.orientation]?.height || 1080) * 100}%`,
                  width: `${(element.size?.width || 0) / (canvas.dimensions?.[canvas.orientation]?.width || 1920) * 100}%`,
                  height: `${(element.size?.height || 0) / (canvas.dimensions?.[canvas.orientation]?.height || 1080) * 100}%`,
                  backgroundColor: element.type === 'text' ? 'rgba(255,255,255,0.8)' : 'rgba(0,0,0,0.3)',
                  borderRadius: 1,
                  border: '1px solid rgba(255,255,255,0.5)',
                }}
              />
            ))}
          </Box>
        )}
        
        {(!canvas.elements || canvas.elements.length === 0) && (
          <Typography
            variant="body2"
            sx={{
              color: 'rgba(255,255,255,0.7)',
              textAlign: 'center',
              px: 2,
            }}
          >
            {canvas.projectName || 'Untitled Menu'}
          </Typography>
        )}
      </Box>
    );
  };

  if (loading && menus.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 4, backgroundColor: colors.neutral.white }}>
        <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)}>
        <Tab
            label="My Projects"
            sx={{
              textTransform: 'none',
              fontWeight: activeTab === 0 ? 600 : 400,
              color: activeTab === 0 ? naturalScale[5] : naturalScale[65],
            }}
          />
          <Tab
            label="Templates"
            sx={{
              textTransform: 'none',
              fontWeight: activeTab === 1 ? 600 : 400,
              color: activeTab === 1 ? naturalScale[5] : naturalScale[65],
            }}
          />
        
        </Tabs>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* My Projects Tab Content */}
      {activeTab === 0 && (
        <>
          {loadingCanvases ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
              <CircularProgress />
            </Box>
          ) : canvases.length === 0 ? (
            <Paper
              sx={{
                p: 8,
                textAlign: 'center',
                backgroundColor: colors.background.default,
                borderRadius: borderRadius.xxxs,
                minHeight: 400,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Typography
                variant="h4"
                fontWeight={700}
                sx={{ mb: 2, color: naturalScale[5] }}
              >
                No projects created yet
              </Typography>
              <Typography
                variant="body1"
                color="text.secondary"
                sx={{ mb: 4, color: naturalScale[65] }}
              >
                Start creating your first design from a blank canvas.
              </Typography>

              <Box
                sx={{
                  mb: 4,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  width: '100%',
                  maxWidth: 200,
                  height: 200,
                }}
              >
                <Box
                  component="img"
                  src={emptyFolderImage}
                  alt="No canvas"
                  sx={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'contain',
                    maxWidth: 200,
                    maxHeight: 200,
                  }}
                />
              </Box>
              <Button
                variant="contained"
                size="large"
                startIcon={<RiArtboard2Line size={20} />}
                onClick={() => setCreateModalOpen(true)}
                sx={{
                  bgcolor: primaryScale[65],
                  color: 'white',
                  textTransform: 'none',
                  fontWeight: 600,
                  px: 4,
                  py: 1.5,
                  '&:hover': {
                    bgcolor: primaryScale[70],
                  },
                }}
              >
                Create Project
              </Button>
            </Paper>
          ) : (
            <Grid container spacing={3}>
              {canvases.map((canvas) => (
                <Grid item xs={12} sm={6} md={4} key={canvas.id}>
                  <Card
                    sx={{
                      '&:hover': {
                        boxShadow: 4,
                      },
                      transition: 'box-shadow 0.3s',
                    }}
                  >
                    <CardMedia component="div">
                      <CanvasPreview canvas={canvas} />
                    </CardMedia>
                    <CardContent>
                      <Typography variant="h6" fontWeight={600} gutterBottom>
                        {canvas.projectName || 'Untitled Menu'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        {canvas.branchName}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {canvas.savedAt 
                          ? new Date(canvas.savedAt).toLocaleDateString() 
                          : 'No date'}
                      </Typography>
                    </CardContent>
                    <CardActions sx={{ justifyContent: 'space-between', px: 2, pb: 2 }}>
                      <Button 
                        size="small" 
                        onClick={() => {
                          dispatch(loadProject(canvas));
                          navigate('/canvas-editor', {
                            state: {
                              projectName: canvas.projectName || 'Untitled Menu',
                              branchId: canvas.branchId,
                              canvasId: canvas.id,
                            },
                          });
                        }}
                        sx={{ textTransform: 'none' }}
                      >
                        Edit
                      </Button>
                      <IconButton
                        size="small"
                        onClick={(e) => handleMenuOpen(e, canvas)}
                        sx={{ ml: 'auto' }}
                      >
                        <MoreVertIcon />
                      </IconButton>
                    </CardActions>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}

          {/* Options Menu */}
          <Menu
            anchorEl={menuAnchorEl}
            open={Boolean(menuAnchorEl)}
            onClose={handleMenuClose}
            PaperProps={{
              sx: {
                borderRadius: borderRadius.xxxs,
                minWidth: 150,
              },
            }}
          >
            <MenuItem onClick={handleEditCanvas}>
              <EditIcon sx={{ mr: 1, fontSize: 20 }} />
              Edit
            </MenuItem>
            <MenuItem onClick={handleDuplicateCanvas}>
              <DuplicateIcon sx={{ mr: 1, fontSize: 20 }} />
              Duplicate
            </MenuItem>
            <MenuItem onClick={handleDeleteCanvas} sx={{ color: 'error.main' }}>
              <DeleteIcon sx={{ mr: 1, fontSize: 20 }} />
              Delete
            </MenuItem>
          </Menu>
        </>
      )}

      {/* Templates Tab Content */}
      {activeTab === 1 && menus.length === 0 ? (
        <Paper
          sx={{
            p: 8,
            textAlign: 'center',
            backgroundColor: colors.background.default,
            borderRadius: borderRadius.xxxs,
            // border: `1px solid ${colors.neutral.gray200}`,
            minHeight: 400,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Typography
            variant="h4"
            fontWeight={700}
            sx={{ mb: 2, color: naturalScale[5] }}
          >
            No canvas created yet
          </Typography>
          <Typography
            variant="body1"
            color="text.secondary"
            sx={{ mb: 4, color: naturalScale[65] }}
          >
            Start creating your first design from a blank canvas.
          </Typography>

          {/* Illustration placeholder - folder with stars */}
          <Box
            sx={{
              mb: 4,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              width: '100%',
              maxWidth: 200,
              height: 200,
            }}
          >
            <Box
              component="img"
              src={emptyFolderImage}
              alt="No canvas"
              sx={{
                width: '100%',
                height: '100%',
                objectFit: 'contain',
                maxWidth: 200,
                maxHeight: 200,
              }}
            />
          </Box>
          <Button
            variant="contained"
            size="large"
            startIcon={<RiArtboard2Line size={20} />}
            onClick={() => setCreateModalOpen(true)}
            sx={{
              bgcolor: primaryScale[65],
              color: 'white',
              textTransform: 'none',
              fontWeight: 600,
              px: 4,
              py: 1.5,
              '&:hover': {
                bgcolor: primaryScale[70],
              },
            }}
          >
            Create Project
          </Button>
        </Paper>
      ) : (
        <Grid container spacing={3}>
          {menus.map((menu) => (
            <Grid item xs={12} sm={6} md={4} key={menu.id}>
              <Card>
                <CardMedia
                  component="div"
                  sx={{
                    height: 200,
                    backgroundColor: 'grey.200',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Typography variant="h6" color="text.secondary">
                    Menu Preview
                  </Typography>
                </CardMedia>
                <CardContent>
                  <Typography variant="h6" fontWeight={600} gutterBottom>
                    {menu.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Template: {menu.template}
                  </Typography>
                  {menu.exportUrl && (
                    <Typography variant="caption" color="success.main">
                      ✓ Exported
                    </Typography>
                  )}
                </CardContent>
                <CardActions>
                  <Button size="small" onClick={() => console.log('Edit menu:', menu.id)}>
                    Edit
                  </Button>
                  <Button
                    size="small"
                    startIcon={<ExportIcon />}
                    onClick={() => handleExportMenu(menu.id)}
                    disabled={exportStatus === 'exporting' || exportStatus === 'uploading'}
                  >
                    Export
                  </Button>
                  <Button
                    size="small"
                    color="error"
                    startIcon={<DeleteIcon />}
                    onClick={() => handleDeleteMenu(menu.id)}
                  >
                    Delete
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* New Project Modal */}
      <Dialog
        open={createModalOpen}
        onClose={() => {
          setCreateModalOpen(false);
          setProjectName('');
          setSelectedBranch('');
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: borderRadius.xxxs,
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            pb: 2,
          }}
        >
          <Typography variant="h6" fontWeight={700} sx={{ color: naturalScale[5] }}>
            New Project
          </Typography>
          <IconButton
            size="small"
            onClick={() => {
              setCreateModalOpen(false);
              setProjectName('');
              setSelectedBranch('');
            }}
            sx={{ color: naturalScale[50] }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Typography
            variant="h6"
            fontWeight={700}
            sx={{ mb: 3, color: naturalScale[5] }}
          >
            Select Takeaway Branch
          </Typography>

          <FormControl fullWidth sx={{ mb: 3 }}>
            <Select
              value={selectedBranch}
              onChange={(e) => setSelectedBranch(e.target.value)}
              displayEmpty
              renderValue={(value) => {
                if (!value) {
                  return (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <RiBuildingLine size={20} color={naturalScale[50]} />
                      <Typography sx={{ color: naturalScale[65] }}>All Branches</Typography>
                    </Box>
                  );
                }
                const selectedBranch = takeawayList?.find(
                  (branch) => (branch.store_id || branch.id) === value
                );
                return (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <RiBuildingLine size={20} color={naturalScale[50]} />
                    <Typography>
                      {selectedBranch?.name || selectedBranch?.store_name || `Branch ${value}`}
                    </Typography>
                  </Box>
                );
              }}
              sx={{
                bgcolor: colors.neutral.white,
                borderRadius: borderRadius.xxxs,
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: colors.neutral.gray200,
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: naturalScale[65],
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: naturalScale[65],
                },
                '& .MuiSelect-select': {
                  py: 1.5,
                  display: 'flex',
                  alignItems: 'center',
                },
              }}
            >
              <MenuItem value="">
                <em>All Branches</em>
              </MenuItem>
              {takeawayList && takeawayList.length > 0 ? (
                takeawayList.map((branch) => (
                  <MenuItem key={branch.store_id || branch.id} value={branch.store_id || branch.id}>
                    {branch.name || branch.store_name || `Branch ${branch.store_id || branch.id}`}
                  </MenuItem>
                ))
              ) : (
                <MenuItem disabled>No branches available</MenuItem>
              )}
            </Select>
          </FormControl>

          <TextField
            fullWidth
            value={projectName}
            onChange={(e) => setProjectName(e.target.value)}
            placeholder="Project Name"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start" sx={{ mr: 1.5 }}>
                  <RiArtboard2Line size={20} color={naturalScale[50]} />
                </InputAdornment>
              ),
            }}
            sx={{
              bgcolor: colors.neutral.white,
              borderRadius: borderRadius.xxxs,
              '& .MuiOutlinedInput-root': {
                '& fieldset': {
                  borderColor: colors.neutral.gray200,
                },
                '&:hover fieldset': {
                  borderColor: naturalScale[65],
                },
                '&.Mui-focused fieldset': {
                  borderColor: naturalScale[65],
                },
                '& input::placeholder': {
                  color: naturalScale[65],
                  opacity: 1,
                },
              },
            }}
          />
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 2 }}>
          <Button
            variant="contained"
            onClick={handleCreateProject}
            disabled={!projectName.trim()}
            fullWidth
            sx={{
              bgcolor: primaryScale[65],
              color: 'white',
              textTransform: 'none',
              fontWeight: 600,
              py: 1.5,
              '&:hover': {
                bgcolor: primaryScale[70],
              },
              '&:disabled': {
                bgcolor: naturalScale[136],
                color: naturalScale[100],
              },
            }}
          >
            Submit
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MenuBuilder;

